import React from 'react';

/** A stored value as text: empty values as "-", objects and lists as JSON, everything else as it was stored. */
export const showValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

/**
 * An extra detail as text. A list of records that carry a name ("label", as on role changes) reads as those names;
 * anything else, including older entries without names, is shown as stored.
 */
export const showDetail = (value: any): string => {
  if (Array.isArray(value) && value.length > 0 && value.every((entry) => typeof entry?.label === 'string')) {
    return value.map((entry) => entry.label).join(', ');
  }
  return showValue(value);
};

/**
 * The platform gives the moment as one text ("03/10/2026 11:30 am"). It is cut at the first space rather than
 * re-read as a date, so the platform's own date format is kept; text without a space stays whole as the date.
 */
export const splitDateTime = (value: string): { date: string; time: string } => {
  const text = (value ?? '').trim();
  if (!text) return { date: '-', time: '-' };
  const space = text.indexOf(' ');
  if (space === -1) return { date: text, time: '-' };
  return { date: text.slice(0, space), time: text.slice(space + 1).trim() || '-' };
};

/** First letter of the first and last word of a name ("Mary Ann Smith" -> "MS"); one word gives its first two letters. */
export const initialsOf = (name: string): string => {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
};

/** A field name with a line-break opportunity after each underscore, so a long name wraps between words, not inside one. */
export const breakable = (name: string): React.ReactNode => {
  const parts = name.split('_');
  return parts.map((part, index) => (
    <React.Fragment key={index}>
      {index < parts.length - 1 ? `${part}_` : part}
      {index < parts.length - 1 && <wbr />}
    </React.Fragment>
  ));
};
